import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, AssociationActivityRow } from "../../../lib/supabase";

export const SUPPORT_PROGRAMS = [
  {
    slug: "ket-noi-doanh-nghiep",
    icon: "ri-exchange-2-line",
    titleKr: "비즈니스 매칭 지원",
    titleVi: "Kết nối doanh nghiệp Hàn–Việt",
    desc: "Tự động ghép cặp doanh nghiệp phù hợp theo ngành hàng và quy mô",
    color: "#004A99",
  },
  {
    slug: "dich-vu-phien-dich",
    icon: "ri-translate-2",
    titleKr: "통번역 서비스",
    titleVi: "Dịch vụ phiên & biên dịch",
    desc: "Hỗ trợ dịch thuật tài liệu kỹ thuật và hợp đồng Hàn–Việt",
    color: "#00AEEF",
  },
  {
    slug: "tu-van-thi-truong",
    icon: "ri-bar-chart-grouped-line",
    titleKr: "시장 진출 컨설팅",
    titleVi: "Tư vấn xâm nhập thị trường",
    desc: "Phân tích thị trường, chiến lược và hỗ trợ pháp lý xuất nhập khẩu",
    color: "#004A99",
  },
  {
    slug: "ho-tro-trien-lam",
    icon: "ri-store-2-line",
    titleKr: "전시회 참가 지원",
    titleVi: "Hỗ trợ tham gia triển lãm",
    desc: "Kết nối gian hàng tại các hội chợ thương mại Hàn Quốc và Việt Nam",
    color: "#00AEEF",
  },
  {
    slug: "tim-kiem-doi-tac",
    icon: "ri-search-eye-line",
    titleKr: "파트너 발굴 지원",
    titleVi: "Tìm kiếm đối tác chiến lược",
    desc: "Phân tích hồ sơ và giới thiệu đối tác phù hợp theo nhu cầu",
    color: "#004A99",
  },
];

const StatsSection = () => {
  const [latestActivity, setLatestActivity] = useState<AssociationActivityRow | null>(null);

  useEffect(() => {
    const fetchLatest = async () => {
      const { data } = await supabase
        .from("association_activities")
        .select("id, title, image_url, focal_point_x, focal_point_y, published_at, category")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) setLatestActivity(data as AssociationActivityRow);
    };
    fetchLatest();
  }, []);

  const formatDate = (d?: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <section className="py-10 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="bg-[#001428] rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* ── Left: Hoạt động Hiệp hội ── */}
            <div className="relative overflow-hidden min-h-[320px] lg:min-h-0 bg-gradient-to-br from-[#0a1f3d] to-[#001428]">
              {/* Subtle background pattern */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-full h-full"
                  style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(0,74,153,0.6) 0%, transparent 70%)" }} />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 lg:p-10 flex flex-col h-full min-h-[320px]">
                {/* Label */}
                <span className="inline-block text-[#00AEEF] text-xs font-bold uppercase tracking-widest mb-6">
                  HOẠT ĐỘNG HIỆP HỘI
                </span>

                {/* Latest article — image in pill/cylinder shape + title below */}
                {latestActivity ? (
                  <div className="flex-1 flex flex-col justify-center items-start gap-4">
                    {/* Pill-shaped image container — 50% larger */}
                    {latestActivity.image_url && (
                      <div
                        className="overflow-hidden flex-shrink-0"
                        style={{
                          width: "100%",
                          maxWidth: "480px",
                          height: "240px",
                          borderRadius: "120px",
                          border: "2px solid rgba(0,174,239,0.3)",
                        }}
                      >
                        <img
                          src={latestActivity.image_url}
                          alt={latestActivity.title}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: `${latestActivity.focal_point_x ?? 50}% ${latestActivity.focal_point_y ?? 50}%` }}
                        />
                      </div>
                    )}
                    {/* Title below pill */}
                    <div>
                      <h2
                        className="text-white font-black leading-tight mb-2"
                        style={{ fontSize: "clamp(16px, 2vw, 22px)" }}
                      >
                        {latestActivity.title}
                      </h2>
                      <p className="text-white/50 text-xs">
                        {formatDate(latestActivity.published_at)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col justify-center">
                    {/* Placeholder pill */}
                    <div
                      className="overflow-hidden flex-shrink-0 mb-4 bg-[#004A99]/30 flex items-center justify-center"
                      style={{ width: "100%", maxWidth: "480px", height: "240px", borderRadius: "120px", border: "2px solid rgba(0,174,239,0.2)" }}
                    >
                      <i className="ri-image-line text-white/20 text-4xl" />
                    </div>
                    <h2 className="text-white font-black text-xl leading-tight mb-2">
                      Nền Tảng B2B<br />
                      <span className="text-[#00AEEF]">Tin Cậy Số 1</span><br />
                      Hàn Quốc – Việt Nam
                    </h2>
                    <p className="text-white/50 text-xs">
                      Cầu nối chính thức giúp doanh nghiệp Hàn Quốc tiếp cận thị trường Việt Nam
                    </p>
                  </div>
                )}

                {/* CTA */}
                <Link
                  to="/association"
                  className="mt-5 inline-flex items-center gap-2 text-[#00AEEF] text-sm font-semibold hover:text-white transition-colors cursor-pointer whitespace-nowrap group"
                >
                  Xem tất cả hoạt động
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* ── Right: Chương trình Hỗ trợ ── */}
            <div className="p-7 lg:p-8 flex flex-col justify-center bg-gradient-to-br from-[#002a5c] to-[#001428]">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1 h-6 bg-[#00AEEF] rounded-full flex-shrink-0" />
                <div>
                  <p className="text-[#00AEEF] text-[10px] font-bold uppercase tracking-widest">한-베 협력 지원 프로그램</p>
                  <h3 className="text-white font-bold text-sm leading-tight">Chương trình Hỗ trợ Hợp tác Hàn - Việt</h3>
                </div>
              </div>
              <p className="text-gray-400 text-xs mb-3 leading-relaxed">
                Chương trình hỗ trợ hợp tác Hàn–Việt toàn diện, giúp doanh nghiệp hai nước kết nối nhanh chóng và hiệu quả.
              </p>
              <div className="space-y-1.5">
                {SUPPORT_PROGRAMS.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/support/${item.slug}`}
                    className="flex items-start gap-2.5 bg-white/5 rounded-lg p-2.5 hover:bg-[#004A99]/40 border border-transparent hover:border-[#00AEEF]/30 transition-all cursor-pointer group"
                  >
                    <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#004A99]/50 rounded-lg group-hover:bg-[#004A99] transition-colors">
                      <i className={`${item.icon} text-[#00AEEF] text-xs`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <p className="text-white text-xs font-bold whitespace-nowrap">{item.titleKr}</p>
                        <p className="text-[#00AEEF] text-[10px] font-medium whitespace-nowrap">{item.titleVi}</p>
                      </div>
                      <p className="text-gray-400 text-[10px] mt-0.5 leading-snug">{item.desc}</p>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-right-line text-[#00AEEF] text-xs" />
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-3 pt-2.5 border-t border-white/10">
                <p className="text-gray-500 text-[10px] text-center">
                  🇰🇷 × 🇻🇳 &nbsp;Hỗ trợ toàn diện – miễn phí cho thành viên đăng ký
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
